/**
 * Dashboard stream of news posts and released versions.
 *
 * The first batch arrives rendered from the server, this module appends the
 * next ones without a reload and answers the live search. The markup it
 * builds mirrors `partials/feed_card.html` exactly, so a card looks the same
 * no matter which of the two rendered it.
 */

import { text } from "../core/i18n";
import { faceImage } from "../core/avatar";
import { query } from "../core/dom";
import { getJson, withQuery } from "../core/http";
import { toast } from "../core/toast";
import type { FeedEntryResponse, FeedResponse } from "../core/types";
import { relativeDate } from "../core/dates";
import { voxelArt } from "../core/art";

const LOADING_CLASS = "is-loading";
const DEBOUNCE_MS = 250;

/** How many entries one batch carries, mirrors ``FEED_PAGE_SIZE``. */
export const BATCH_SIZE = 5;

const ENDPOINT = "/api/feed";

/** Wording of a card, taken from the texts the page carries. */
export interface FeedLabels {
    news: string;
    read: string;
    changelog: string;
    minutes: string;
    empty: string;
    error: string;
    allShown: string;
}

/**
 * Return the wording of a card in the language of the page.
 *
 * The first batch arrives rendered by the server, the next ones are built
 * here, so both have to speak the language picked in the header.
 */
export function readLabels(): FeedLabels {
    return {
        news: text("feed.news", "Novinka"),
        read: text("feed.read", "Čítať príspevok"),
        changelog: text("feed.changelog", "Zoznam zmien"),
        minutes: text("feed.minutes", "min čítania"),
        empty: text("feed.no_results", "Žiadny príspevok nezodpovedá zadanému hľadaniu."),
        error: text("feed.error", "Ďalšie príspevky sa nepodarilo načítať."),
        allShown: text("feed.all_shown", "Zobrazili ste všetko, čo tu máme."),
    };
}

let labels: FeedLabels = readLabels();

/** Return the icon markup used inside a card. */
function iconMarkup(name: string, size: number): string {
    return (
        `<svg class="icon" width="${size}" height="${size}" aria-hidden="true">` +
        `<use href="/static/assets/icons/icons.svg#${name}"></use></svg>`
    );
}

/** Append a text node wrapped in `tag` with `className` to `parent`. */
function addText(parent: HTMLElement, tag: string, className: string, text: string): HTMLElement {
    const element = document.createElement(tag);
    element.className = className;
    element.textContent = text;
    parent.appendChild(element);

    return element;
}

/** How large the face beside an author of the stream is drawn. */
const AVATAR_SIZE = 18;

/** Return the small avatar shown in front of the author, as the template does. */
function avatarImage(username: string): HTMLImageElement {
    return faceImage(username, AVATAR_SIZE);
}

/** Append a counter of the footer, an icon followed by its value. */
function addStat(parent: HTMLElement, name: string, text: string): HTMLElement {
    const stat = document.createElement("span");
    stat.className = "feed-card__stat";
    stat.innerHTML = iconMarkup(name, 12);
    stat.append(` ${text}`);
    parent.appendChild(stat);

    return stat;
}

/** Return the media column of a card. */
function renderMedia(entry: FeedEntryResponse): HTMLElement {
    const media = document.createElement("a");
    media.className = "feed-card__media";
    media.href = entry.url;
    media.setAttribute("aria-label", entry.title);

    if (entry.image_url) {
        const image = document.createElement("img");
        image.className = "feed-card__image";
        image.src = entry.image_url;
        image.alt = entry.title;
        image.loading = "lazy";
        media.appendChild(image);
    } else {
        const fallback = document.createElement("span");
        fallback.className = "feed-card__fallback";
        fallback.setAttribute("aria-hidden", "true");

        const artwork = document.createElement("img");
        artwork.src = voxelArt(entry.id);
        artwork.alt = "";
        artwork.width = 180;
        artwork.height = 180;
        artwork.loading = "lazy";
        fallback.appendChild(artwork);
        addText(fallback, "span", "", entry.kind === "release" ? (entry.version ?? "") : entry.category);
        media.appendChild(fallback);
    }

    const kind = document.createElement("span");
    kind.className = "feed-card__kind";
    kind.innerHTML = iconMarkup(entry.kind === "release" ? "tag" : "file-text", 11);
    kind.append(` ${entry.kind === "release" ? (entry.version ?? "") : labels.news}`);
    media.appendChild(kind);

    return media;
}

/** Return the footer of a card with its buttons and counters. */
function renderFooter(entry: FeedEntryResponse): HTMLElement {
    const footer = document.createElement("div");
    footer.className = "feed-card__footer";

    const open = document.createElement("a");
    open.className = "button button--small button--primary";
    open.href = entry.url;
    open.textContent = entry.kind === "release" ? labels.changelog : labels.read;
    open.insertAdjacentHTML("beforeend", iconMarkup("arrow-right", 13));
    footer.appendChild(open);

    const stats = document.createElement("span");
    stats.className = "feed-card__stats";

    if (entry.reading_minutes > 0) {
        addStat(stats, "clock", `${entry.reading_minutes} ${labels.minutes}`);
    }

    if (entry.kind !== "release") {
        addStat(stats, "eye", String(entry.view_count));
    }

    footer.appendChild(stats);

    return footer;
}

/** Return the whole card of a single feed entry. */
export function renderFeedCard(entry: FeedEntryResponse): HTMLElement {
    const card = document.createElement("article");
    card.className = `feed-card feed-card--${entry.kind}`;
    card.appendChild(renderMedia(entry));

    const body = document.createElement("div");
    body.className = "feed-card__body";

    const meta = document.createElement("div");
    meta.className = "feed-card__meta";
    addText(meta, "span", "post-chip", entry.category);

    const byline = document.createElement("span");
    byline.className = "feed-card__byline";
    const author = addText(byline, "span", "feed-card__author", ` ${entry.author}`);
    author.prepend(avatarImage(entry.author));
    const date = addText(byline, "time", "feed-card__date", relativeDate(entry.published_at));
    date.setAttribute("datetime", entry.published_at);
    meta.appendChild(byline);
    body.appendChild(meta);

    const title = document.createElement("h3");
    title.className = "feed-card__title";
    const link = document.createElement("a");
    link.href = entry.url;
    link.textContent = entry.title;
    title.appendChild(link);
    body.appendChild(title);

    addText(body, "p", "feed-card__summary", entry.summary);

    body.appendChild(renderFooter(entry));
    card.appendChild(body);

    return card;
}

/** Replace the whole stream with `entries`. */
export function renderFeed(list: HTMLElement, entries: readonly FeedEntryResponse[]): void {
    list.innerHTML = "";

    if (entries.length === 0) {
        const empty = document.createElement("p");
        empty.className = "empty-state";
        empty.textContent = labels.empty;
        list.appendChild(empty);

        return;
    }

    appendFeed(list, entries);
}

/** Append `entries` to the end of the stream. */
export function appendFeed(list: HTMLElement, entries: readonly FeedEntryResponse[]): void {
    for (const entry of entries) {
        list.appendChild(renderFeedCard(entry));
    }
}

/** Show or hide the subtle busy state of the stream. */
export function setFeedLoading(list: HTMLElement, loading: boolean): void {
    list.classList.toggle(LOADING_CLASS, loading);
    list.setAttribute("aria-busy", loading ? "true" : "false");
}

/** Return the URL of one batch of the feed. */
export function feedUrl(offset: number, search: string, category: string): string {
    return withQuery(ENDPOINT, { offset, limit: BATCH_SIZE, search, category });
}

/** Wire the button which appends the next batch under the stream. */
function initLoadMore(list: HTMLElement): void {
    const holder = query<HTMLElement>("[data-feed-more]");
    const button = query<HTMLAnchorElement>("[data-feed-next]");

    if (!holder || !button) {
        return;
    }

    const search = query<HTMLInputElement>("[data-feed-search]");
    let offset = Number.parseInt(button.dataset["feedOffset"] ?? "0", 10) || BATCH_SIZE;
    let busy = false;

    button.addEventListener("click", (event: Event) => {
        event.preventDefault();

        if (busy) {
            return;
        }

        busy = true;
        button.classList.add("is-disabled");
        setFeedLoading(list, true);

        const url = feedUrl(offset, search?.value.trim() ?? "", search?.dataset["feedCategory"] ?? "");

        void getJson<FeedResponse>(url)
            .then((payload) => {
                appendFeed(list, payload.items);
                offset += payload.items.length;

                if (!payload.has_more) {
                    holder.innerHTML = "";
                    addText(holder, "p", "feed-more__done", labels.allShown);
                }
            })
            .catch(() => toast(labels.error, "error"))
            .finally(() => {
                busy = false;
                button.classList.remove("is-disabled");
                setFeedLoading(list, false);
            });
    });
}

/** Wire the search box replacing the stream while the visitor types. */
function initSearch(list: HTMLElement): void {
    const input = query<HTMLInputElement>("[data-feed-search]");
    if (!input) {
        return;
    }

    const initialMarkup = list.innerHTML;
    let timer: ReturnType<typeof setTimeout> | undefined;

    input.addEventListener("input", () => {
        if (timer !== undefined) {
            clearTimeout(timer);
        }

        timer = setTimeout(() => {
            const term = input.value.trim();
            const holder = query<HTMLElement>("[data-feed-more]");

            if (term === "") {
                list.innerHTML = initialMarkup;
                setFeedLoading(list, false);

                return;
            }

            setFeedLoading(list, true);

            void getJson<FeedResponse>(feedUrl(0, term, input.dataset["feedCategory"] ?? ""))
                .then((payload) => {
                    renderFeed(list, payload.items);
                    // The search returns its own page, the button of the
                    // stream would append the unfiltered rest under it.
                    if (holder) {
                        holder.innerHTML = "";
                    }
                })
                .catch(() => toast(labels.error, "error"))
                .finally(() => setFeedLoading(list, false));
        }, DEBOUNCE_MS);
    });
}

/** Wire the dashboard stream when the page has one. */
export function initFeed(): void {
    const list = query<HTMLElement>("[data-feed-list]");
    if (!list) {
        return;
    }

    labels = readLabels();
    initLoadMore(list);
    initSearch(list);
}
